import { MachineContext } from 'contexts/machine.context';
import { PITCH_TYPE_OPTIONS, TrainingStatus } from 'lib_ts/enums/pitches.enums';
import {
  DEFAULT_MX_SUMMARY,
  IPitchList,
  safeFolder,
} from 'lib_ts/interfaces/pitches/i-pitch-list';
import { clone } from 'lodash';
import { useContext, useEffect } from 'react';
import useListLibraryStore from 'stores/use-list-library-store';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';

/** ensure that any repeated instances of FOLDER_SEPARATOR are collapsed into no more than 1 */
const sanitizeFolders = (lists: IPitchList[]) =>
  lists.map((list) => {
    const out = Object.assign({}, list);
    out.folder = safeFolder(list.folder);
    return out;
  });

const useMixinListSummaries = () => {
  const { machine } = useContext(MachineContext);

  const rawLists = useListLibraryStore((s) => s.rawLists);
  const listSummaryDict = useListLibraryStore((s) => s.listSummaryDict);

  const tmsDict = useTrainedMsStore((s) => s.tmsDict);

  useEffect(() => {
    for (const l of rawLists) {
      const summary = listSummaryDict[l._id];

      if (!summary) {
        l._mx_summary = clone(DEFAULT_MX_SUMMARY);
        continue;
      }

      // Sort the pitch type badges the same way they are sorted in our Pitch Type filters
      summary.types = summary.types.sort((a, b) => {
        const indexA = PITCH_TYPE_OPTIONS.findIndex((v) => v.value === a);
        const indexB = PITCH_TYPE_OPTIONS.findIndex((v) => v.value === b);
        return indexA - indexB;
      });

      // count hashes that are trained according to matching context
      summary.trained = summary.bs_hashes.filter(
        (h) => tmsDict[h]?._mx_trained
      ).length;

      summary.trained_percent = summary.trained / Math.max(summary.total, 1);

      // set the training status for use later
      summary.training_status =
        summary.trained === 0
          ? TrainingStatus.Not
          : summary.trained === summary.total
            ? TrainingStatus.Full
            : TrainingStatus.Partial;

      // tie the summary to the list
      l._mx_summary = summary;
    }

    // fix folder paths
    useListLibraryStore.setState({ lists: sanitizeFolders(rawLists) });
  }, [rawLists, listSummaryDict, tmsDict, machine.last_hardware_changed]);
};

export default useMixinListSummaries;
